import { FC } from "react";
import { PLAID_INSTITUTIONS_NAMES } from "@common/constants";
import { Entity } from "@common/types/entity";
import { AdpRunCommandItem } from "@components/adpRun/AdpRunCommandItem";
import { CreateGustoConnection } from "@components/gusto/CreateGustoConnection";
import { CreateMercuryConnection } from "@components/mercury/CreateMercuryConnection";
import { ConnectPlaid } from "@components/plaid/ConnectPlaid/ConnectPlaid";
import { CreateRampConnection } from "@components/ramp/CreateRampConnection";
import { CreateStripeConnection } from "@components/stripe/CreateStripeConnection";
import { CreatePayPalConnection } from "@components/paypal/CreatePayPalConnection";
import { CreateBillcomConnection } from "@components/billcom/CreateBillcomConnection";
import { Tag } from "@design-system/Tag/Tag";
import { InstitutionIcon } from "@design-system/InstitutionIcon/InstitutionIcon";
import { CommandItem } from "@design-system/CommandItem/CommandItem";

interface AccountsIntegrationCommandItemsProps {
    defaultEntity?: Entity;
    onConnected: () => void;
    onSelectGusto: () => void;
    onSelectRelay: () => void;
}

export const AccountsIntegrationCommandItems: FC<
    AccountsIntegrationCommandItemsProps
> = ({ defaultEntity, onConnected, onSelectGusto, onSelectRelay }) => (
    <>
        <CreateStripeConnection
            onCreated={onConnected}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                >
                    <InstitutionIcon institution="stripe" size="sm" /> Stripe
                </CommandItem>
            )}
        </CreateStripeConnection>

        <CreateGustoConnection
            onCreated={onConnected}
            defaultEntity={defaultEntity}
        >
            {({ connecting }) => (
                <CommandItem onSelect={onSelectGusto} disabled={connecting}>
                    <InstitutionIcon institution="gusto" size="sm" /> Gusto
                    <Tag color="neutral" size="sm">
                        Recommended
                    </Tag>
                </CommandItem>
            )}
        </CreateGustoConnection>

        <AdpRunCommandItem
            defaultEntity={defaultEntity}
            onConnected={onConnected}
        />

        <CommandItem keywords={["relay"]} onSelect={() => onSelectRelay()}>
            <InstitutionIcon institution="relay" size="sm" /> Relay{" "}
            <Tag color="neutral" size="sm">
                Recommended
            </Tag>
        </CommandItem>

        <CreateMercuryConnection
            onCreated={onConnected}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["mercury"]}
                >
                    <InstitutionIcon institution="mercury" size="sm" /> Mercury
                </CommandItem>
            )}
        </CreateMercuryConnection>

        <CreatePayPalConnection onCreated={onConnected}>
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                >
                    <InstitutionIcon institution="paypal" size="sm" /> PayPal
                </CommandItem>
            )}
        </CreatePayPalConnection>

        <CreateRampConnection
            onCreated={onConnected}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                >
                    <InstitutionIcon institution="ramp" size="sm" /> Ramp
                </CommandItem>
            )}
        </CreateRampConnection>

        <CreateBillcomConnection
            onCreated={onConnected}
            defaultEntity={defaultEntity}
        >
            {({ isConnecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={isConnecting}
                    keywords={["bill", "bill.com", "billcom"]}
                >
                    <InstitutionIcon institution="bill" size="sm" /> BILL
                </CommandItem>
            )}
        </CreateBillcomConnection>

        <ConnectPlaid
            onConnected={onConnected}
            institutionName={PLAID_INSTITUTIONS_NAMES.CHASE}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["chase"]}
                >
                    <InstitutionIcon institution="chase" size="sm" /> Chase
                </CommandItem>
            )}
        </ConnectPlaid>

        <ConnectPlaid
            onConnected={onConnected}
            institutionName={PLAID_INSTITUTIONS_NAMES.AMERICAN_EXPRESS}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["american express", "amex"]}
                >
                    <InstitutionIcon institution="amex" size="sm" /> American
                    Express{" "}
                </CommandItem>
            )}
        </ConnectPlaid>

        <ConnectPlaid
            onConnected={onConnected}
            institutionName={PLAID_INSTITUTIONS_NAMES.CAPITAL_ONE}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["capital one"]}
                >
                    <InstitutionIcon institution="capital-one" size="sm" />{" "}
                    Capital One{" "}
                </CommandItem>
            )}
        </ConnectPlaid>

        <ConnectPlaid
            onConnected={onConnected}
            institutionName={PLAID_INSTITUTIONS_NAMES.BANK_OF_AMERICA}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["bank of america", "boa"]}
                >
                    <InstitutionIcon institution="bank-of-america" size="sm" />{" "}
                    Bank of America{" "}
                </CommandItem>
            )}
        </ConnectPlaid>

        <ConnectPlaid
            onConnected={onConnected}
            institutionName={PLAID_INSTITUTIONS_NAMES.WELLS_FARGO}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["wellsfargo", "wells fargo", "wells", "fargo"]}
                >
                    <InstitutionIcon institution="wells-fargo" size="sm" />{" "}
                    Wells Fargo
                </CommandItem>
            )}
        </ConnectPlaid>

        <ConnectPlaid
            onConnected={onConnected}
            institutionName={PLAID_INSTITUTIONS_NAMES.CITIBANK}
            defaultEntity={defaultEntity}
        >
            {({ connecting, initializeConnection }) => (
                <CommandItem
                    onSelect={initializeConnection}
                    disabled={connecting}
                    keywords={["citibank", "citi bank", "citi"]}
                >
                    <InstitutionIcon institution="citi" size="sm" /> Citibank
                </CommandItem>
            )}
        </ConnectPlaid>
    </>
);
